import React from "react";
import {
  Users,
  Megaphone,
  Cpu,
  Briefcase,
  Scale,
  Calendar,
} from "lucide-react";

const councils = [
  {
    id: 1,
    icon: <Briefcase size={32} className="text-[#4FB1FF]" />,
    title: "Business Advisory Council",
    subtitle: "Charting Your Path to Strategic Growth",
    description:
      "Offering bespoke consulting and strategic coaching to help businesses—from startups to multinationals—navigate global expansion, market diversification, and sustainable growth.",
  },
  {
    id: 2,
    icon: <Megaphone size={32} className="text-[#4FB1FF]" />,
    title: "Marketing & Communications Council",
    subtitle: "Crafting Influence Through Strategic Visibility",
    description:
      "Helping members build culturally resonant brands, effective marketing campaigns, and communication strategies that amplify visibility across Indo European markets.",
  },
  {
    id: 3,
    icon: <Cpu size={32} className="text-[#4FB1FF]" />,
    title: "IT, Innovation & Digitization Council",
    subtitle: "Transforming Businesses for the Digital Age",
    description:
      "Guiding members through digital adoption, cybersecurity, and innovation—bridging traditional operations with cutting-edge technologies and compliance standards.",
  },
  {
    id: 4,
    icon: <Users size={32} className="text-[#4FB1FF]" />,
    title: "Human Capital & Leadership Council",
    subtitle: "Empowering People, Building Legacy",
    description:
      "Focusing on leadership, inclusion, and workforce transformation to help organizations nurture purpose-driven cultures and resilient teams.",
  },
  {
    id: 5,
    icon: <Scale size={32} className="text-[#4FB1FF]" />,
    title: "Economic Affairs, Taxation & Legal Council",
    subtitle: "Navigating Global Trade, Policy & Risk",
    description:
      "Offering expert guidance on taxation, ESG, FDI, and legal frameworks—ensuring compliance, risk mitigation, and global competitiveness.",
  },
  {
    id: 6,
    icon: <Calendar size={32} className="text-[#4FB1FF]" />,
    title: "Events & Industry Engagement Council",
    subtitle: "Where Strategic Presence Meets Memorable Impact",
    description:
      "Transforming events into strategic opportunities through curated forums, investor meets, and global trade collaborations that elevate visibility and impact.",
  },
];

const AdvisoryCouncilsSection = () => {
  return (
    <section className="bg-white py-20 px-6">
      <div className="container mx-auto text-center max-w-6xl">
        {/* Section Header */}
        <h5 className="text-[#4FB1FF]  uppercase font-semibold tracking-wide mb-3">
          Advisory Councils
        </h5>
        <h2 className="text-4xl md:text-6xl  font-Agatho_Regular  font-bold text-[#0B1D33] mb-6 leading-tight">
          Strategic Expertise to Empower Every Dimension of Business
        </h2>
        <p className="text-gray-600 max-w-3xl mx-auto mb-16 leading-relaxed">
          <strong>Indo European Business Council</strong> connects industry leaders,
          innovators, and policymakers through specialized councils that guide
          our members with strategic foresight, actionable insight, and trusted
          mentorship. Each council drives transformation in its own domain—
          uniting knowledge, experience, and impact.
        </p>
   

        {/* Cards Container */}
        <div className="space-y-16 mt-10">
          {councils.map((council, index) => (
            <div
              key={council.id}
              className={`relative flex flex-col md:flex-row items-start md:items-center gap-8 md:gap-12 ${
                index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
              }`}
            >
              {/* Icon & Accent Line */}
              <div className="flex flex-col items-center relative">
                <div className="bg-white p-4 rounded-2xl shadow-lg border border-[#E3EEFF]">
                  {council.icon}
                </div>
                {index !== councils.length - 1 && (
                  <div className="hidden md:block w-[2px] h-24 bg-gradient-to-b from-[#4FB1FF]/30 to-transparent mt-3" />
                )}
              </div>

              {/* Content Box */}
              <div
                className={`flex-1 bg-[#F7FAFF] rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 border-l-4 ${
                  index % 2 === 0 ? "border-[#4FB1FF]" : "border-[#0B1D33]"
                }`}
              >
                <h3 className="text-3xl  font-Agatho_Regular  font-bold text-[#0B1D33] mb-1">
                  {council.title}
                </h3>
                <h4
                  className={`text-sm font-semibold mb-4 ${
                    index % 2 === 0 ? "text-[#4FB1FF]" : "text-[#0B1D33]"
                  }`}
                >
                  {council.subtitle}
                </h4>
                <p className="text-gray-600 text-base leading-relaxed mb-6">
                  {council.description}
                </p>

                {/* <button
                  className={`inline-flex items-center gap-2 text-sm font-semibold px-5 py-2 rounded-full transition ${
                    index % 2 === 0
                      ? "bg-[#4FB1FF] text-white hover:bg-[#3A93D9]"
                      : "bg-[#0B1D33] text-white hover:bg-[#1E3352]"
                  }`}
                >
                  Learn More <span className="text-lg">→</span>
                </button> */}
              </div>
            </div>
          ))}
        </div>

        {/* Why Our Councils Matter */}
        <div className="mt-20 bg-[#0B1D33] text-white rounded-2xl p-10 shadow-lg text-left">
          <h3 className="text-3xl md:text-4xl  font-Agatho_Regular  font-bold mb-4">
            ✦ Why Our Councils Matter
          </h3>
          <ul className="space-y-4 text-gray-200">
            <li>
              <strong className="text-[#4FB1FF]">Integrated Expertise:</strong>{" "}
              Councils blend insights from business leaders, policymakers, and
              domain experts to provide comprehensive guidance.
            </li>
            <li>
              <strong className="text-[#4FB1FF]">Tailored Solutions:</strong>{" "}
              Each engagement is customized to your sector, scale, and strategic
              goals—ensuring relevant, actionable impact.
            </li>
            <li>
              <strong className="text-[#4FB1FF]">Bilateral Advantage:</strong>{" "}
              Our Indo European perspective gives members unparalleled access to
              cross-border opportunities and collaborations.
            </li>
          </ul>
          <p className="mt-6 text-gray-300 leading-relaxed">
            Let the <strong>Indo European Business Council</strong> Advisory Councils be your
            strategic co-pilots— supporting your journey, challenging your
            limits, and helping you unlock new frontiers of success.
          </p>
     
        </div>
      </div>
    </section>
  );
};

export default AdvisoryCouncilsSection;
